import {Typography} from "@mui/material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import type {DataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import type {SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";
import {getSupplierNormalizedColumns} from "./SupplierNormalizedColumns";
import {supplierGridInitialState} from "~/pages/supplier-360/components/supplierGridInitialState";

export type SupplierNormalizedTableProps = {
    rows: SupplierEntity[];
    isLoading: boolean;
    rowCount: number;
    /** Pagination / filter / sort state from `useDataGridQueryState`. */
    gridState: DataGridQueryState;
    /** Page-level search term in effect — narrows the rows without going through `filterModel`. */
    isSearchActive?: boolean;
    onOpenEntity: (entityId: number) => void;
    title?: string;
};

export const SupplierNormalizedTable = ({
                                            rows,
                                            isLoading,
                                            rowCount,
                                            gridState,
                                            isSearchActive = false,
                                            onOpenEntity,
                                            title,
                                        }: SupplierNormalizedTableProps) => {
    const {hasActiveFilters} = gridState;
    // A search narrows the rows just like a column filter, so an empty result is "no match", not "no data".
    const isNarrowed = hasActiveFilters || isSearchActive;

    if (rows.length === 0 && !isLoading && !isNarrowed) {
        return <Typography color="text.secondary">No suppliers found.</Typography>;
    }

    return (
        <CustomDataGrid
                layoutKey="supplier-360-normalized"
                initialState={supplierGridInitialState('canonical_name', ['entity_type', 'confidence'])}
                rows={rows}
                columns={getSupplierNormalizedColumns()}
                loading={isLoading}
                rowCount={rowCount}
                {...gridState.dataGridProps}
                // Text and number filters run server-side, so don't fire one request per keystroke.
                filterDebounceMs={500}
                showToolbar
                disableRowSelectionOnClick
                onRowClick={({row}) => onOpenEntity(row.id)}
                sx={{cursor: 'pointer'}}
                toolbarProps={{
                    title: title,
                    showFilterPanel: false,
                }}
                density="standard"
                localeText={isNarrowed && rows.length === 0 && !isLoading
                    ? {noRowsLabel: 'No suppliers match the current filters.'}
                    : undefined}
        />
    );
};
